import { useEffect } from 'react';
import { Loader2, ShieldAlert, Search, Info, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AiGeneratedBadge } from '@/components/AiGeneratedBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useChallengeToken, type ChallengeTokenInput, type ChallengeSeverity } from '@/hooks/useChallengeToken';

interface ChallengeTabProps {
  input: ChallengeTokenInput;
  active: boolean;
}

const severityStyles: Record<ChallengeSeverity, string> = {
  low: 'bg-success/15 text-success border-success/30',
  moderate: 'bg-warning/15 text-warning border-warning/30',
  elevated: 'bg-destructive/15 text-destructive border-destructive/30',
};

export const ChallengeTab = ({ input, active }: ChallengeTabProps) => {
  const { report, isLoading, error, runChallenge } = useChallengeToken();

  useEffect(() => {
    if (active && !report && !isLoading && !error && input.symbol) {
      runChallenge(input);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, input.symbol]);

  return (
    <div className="space-y-6">
      <AiGeneratedBadge />

      {/* Disclaimer banner */}
      <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/5 p-4">
        <Info className="w-5 h-5 text-warning shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">
          This is a structured risk assessment — not a prediction or recommendation. It is
          designed to surface considerations worth investigating before making any decision.
        </p>
      </div>

      {isLoading && (
        <Card>
          <CardContent className="py-12 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-warning" />
            <p className="text-sm text-muted-foreground">Running adversarial analysis...</p>
          </CardContent>
        </Card>
      )}

      {error && !isLoading && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="py-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-foreground mb-3">
                {error || 'The risk analysis could not be completed for this token. Please try again.'}
              </p>
              <Button size="sm" variant="outline" onClick={() => runChallenge(input)}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {report && !isLoading && (
        <>
          {/* Overall summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-warning" />
                Overall risk observations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {report.overall_risk_summary}
              </p>
            </CardContent>
          </Card>

          {/* Risk dimensions */}
          <div className="space-y-3">
            {report.risk_dimensions?.map((dim) => (
              <Card key={dim.id}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <h4 className="text-sm font-semibold text-foreground">{dim.label}</h4>
                    <Badge variant="outline" className={severityStyles[dim.severity] ?? ''}>
                      {dim.severity}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                    {dim.observation}
                  </p>
                  {dim.data_point && (
                    <p className="text-xs font-mono text-muted-foreground/80 mt-2 pt-2 border-t border-border">
                      {dim.data_point}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Bear case */}
          {report.bear_case_scenario && (
            <Card className="border-l-4 border-l-warning">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">One scenario worth considering</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {report.bear_case_scenario}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Questions */}
          {report.questions_to_investigate?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Questions worth researching</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-2">
                  {report.questions_to_investigate.map((q, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Search className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                      <span><span className="font-semibold text-foreground">{i + 1}.</span> {q}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}

          {/* Footer */}
          <div className="text-xs text-muted-foreground space-y-1 px-1">
            <p>Generated: {new Date(report.generated_at).toLocaleString('en-AU')}</p>
            <p>{report.disclaimer}</p>
          </div>
        </>
      )}
    </div>
  );
};
